import {PendingUnderwritingRequirementDto, PolicyDto} from 'Agent/Policies/api';
import {PolicyImages} from 'Agent/Policies/sagas/policySaga';
import {Async, AsyncPage, Empty} from 'Core/models/Async';
import {DateRange, EmptyDateRange} from 'Core/models/DateRange';
import {FilterOptionUnion, FilterOptionValue} from 'Core/models/FilterOption';
import {Page} from 'Core/models/Page';
import * as O from 'fp-ts/lib/Option';
import {Sort} from 'Shared/components/PagedView/Table/Sort';
import {createAction} from 'Shared/utilities/actionCreator';
import {ValidAgentNumber} from 'Users/models/AgentNumber';
import {PolicyDetailDto} from '../models/Policy';

export enum PolicyActionTypes {
  FETCH_POLICIES = 'policy/FETCH_POLICIES',
  FETCH_POLICY = 'policy/FETCH_POLICY',
  FETCH_POLICY_IMAGES = 'policy/FETCH_POLICY_IMAGES',
  FETCH_PENDING_UNDERWRITING_REQUIREMENTS = 'policy/FETCH_PENDING_UNDERWRITING_REQUIREMENTS',
  CHANGE_POLICY_IMAGES = 'policy/CHANGE_POLICY_IMAGES',
  CHANGE_POLICIES = 'policy/CHANGE_POLICIES',
  CHANGE_POLICY = 'policy/CHANGE_POLICY',
  CHANGE_POLICY_FILTER = 'policy/CHANGE_POLICY_FILTER',
  CHANGE_SORT = 'policy/CHANGE_SORT',
  CLEAR_SORT = 'policy/CLEAR_SORT',
  CHANGE_PENDING_UNDERWRITING_REQUIREMENTS = 'policy/CHANGE_PENDING_UNDERWRITING_REQUIREMENTS',
}

export const EmptyPolicyFilter: PolicyFilter = {
  accountNumber: '',
  policyType: FilterOptionValue.EmptyOption(),
  insuredName: '',
  payorName: '',
  policyNumber: '',
  premium: '',
  issueDate: EmptyDateRange,
  effectiveDate: EmptyDateRange,
  status: FilterOptionValue.EmptyOption(),
  sourceId: FilterOptionValue.EmptyOption(),
  accountName: '',
  last4PayorNumber: '',
  ownerLast4OfSSN: '',
};

export type PolicyFilter = {
  pageSize?: number;
  accountNumber: string;
  policyNumber: string;
  payorName: string;
  premium: string;
  insuredName: string;
  policyType: FilterOptionUnion;
  issueDate: DateRange;
  effectiveDate: DateRange;
  sourceId: FilterOptionUnion;
  status: FilterOptionUnion;
  accountName: string;
  last4PayorNumber: string;
  ownerLast4OfSSN: string;
  isExact?: boolean;
};

export const changeSort = (sort?: Sort) => createAction(PolicyActionTypes.CHANGE_SORT, {sort});
export const clearSort = () => createAction(PolicyActionTypes.CLEAR_SORT);
export const fetchPolicyImages = (encryptedIdentifier: string, policyNumber: string, policyId: string, source: string) =>
  createAction(PolicyActionTypes.FETCH_POLICY_IMAGES, {encryptedIdentifier, policyNumber, policyId, source});
export const fetchPolicies = (
  agentNumbers: ValidAgentNumber[],
  isNewYork: boolean,
  encryptedIdentifier: string,
  page?: Page<PolicyDto>,
  filter?: PolicyFilter,
  sort?: Sort
) => {
  return createAction(PolicyActionTypes.FETCH_POLICIES, {
    agentNumbers,
    isNewYork,
    encryptedIdentifier,
    filter,
    pageOption: page,
    sort,
  });
};
export const fetchPendingUnderwritingRequirements = (
  encryptedIdentifier: string,
  agentNumbers: string[],
  isNewYork: boolean
) =>
  createAction(PolicyActionTypes.FETCH_PENDING_UNDERWRITING_REQUIREMENTS, {
    encryptedIdentifier,
    agentNumbers,
    isNewYork,
  });
export const changePolicyFilter = (policyFilter: PolicyFilter) =>
  createAction(PolicyActionTypes.CHANGE_POLICY_FILTER, policyFilter);
export const changePolicies = (policies: AsyncPage<Page<PolicyDto>>) =>
  createAction(PolicyActionTypes.CHANGE_POLICIES, policies);
export const clearPolicies = () => createAction(PolicyActionTypes.CLEAR_SORT);
export const fetchPolicy = (encryptedIdentifier: string, policyId: string, policyNumber: string, source: string, producerCodes: ValidAgentNumber[]) =>
  createAction(PolicyActionTypes.FETCH_POLICY, {policyId, encryptedIdentifier, policyNumber, source, producerCodes});
export const changePolicy = (policy: Async<PolicyDto>) => createAction(PolicyActionTypes.CHANGE_POLICY, policy);
export const clearPolicy = () => createAction(PolicyActionTypes.CHANGE_POLICY, DEFAULT_STATE.policy);
export const clearPolicyFilters = () =>
  createAction(PolicyActionTypes.CHANGE_POLICY_FILTER, DEFAULT_STATE.policyFilter);
export const changePolicyImages = (policyImages: Async<PolicyImages>) =>
  createAction(PolicyActionTypes.CHANGE_POLICY_IMAGES, policyImages);
export const changePendingUnderwritingRequirements = (
  pendingUnderwritingRequirements: Async<PendingUnderwritingRequirementDto[]>
) => createAction(PolicyActionTypes.CHANGE_PENDING_UNDERWRITING_REQUIREMENTS, pendingUnderwritingRequirements);

type PolicyActions =
  | ReturnType<typeof clearSort>
  | ReturnType<typeof changeSort>
  | ReturnType<typeof changePolicyFilter>
  | ReturnType<typeof changePolicies>
  | ReturnType<typeof changePolicyImages>
  | ReturnType<typeof changePolicy>
  | ReturnType<typeof changePendingUnderwritingRequirements>;

type PolicyState = {
  policies: AsyncPage<Page<PolicyDto>>;
  policy: Async<PolicyDetailDto>;
  policyFilter: PolicyFilter;
  policyImages: Async<PolicyImages>;
  pendingUnderwritingRequirements: Async<PendingUnderwritingRequirementDto[]>;
  sort: O.Option<Sort>;
};

export const DEFAULT_STATE = {
  policies: new Empty(),
  policy: new Empty(),
  policyFilter: EmptyPolicyFilter,
  policyImages: new Empty(),
  pendingUnderwritingRequirements: new Empty(),
  sort: O.none,
};

export default (state: PolicyState = DEFAULT_STATE, action: PolicyActions): PolicyState => {
  switch (action.type) {
    case PolicyActionTypes.CLEAR_SORT:
      return {
        ...state,
        sort: DEFAULT_STATE.sort,
      };
    case PolicyActionTypes.CHANGE_SORT:
      return {
        ...state,
        sort: O.fromNullable(action.payload.sort),
      };
    case PolicyActionTypes.CHANGE_POLICY_IMAGES:
      return {
        ...state,
        policyImages: action.payload,
      };
    case PolicyActionTypes.CHANGE_POLICY:
      return {
        ...state,
        policy: action.payload,
      };
    case PolicyActionTypes.CHANGE_POLICY_FILTER:
      return {
        ...state,
        policyFilter: action.payload,
      };
    case PolicyActionTypes.CHANGE_POLICIES:
      return {
        ...state,
        policies: action.payload,
      };
    case PolicyActionTypes.CHANGE_PENDING_UNDERWRITING_REQUIREMENTS:
      return {
        ...state,
        pendingUnderwritingRequirements: action.payload,
      };
    default:
      return state;
  }
};

export type FilterEnum = {
  name: string;
  value: string;
};

export const PolicyStatuses: FilterEnum[] = [
         {
           name: 'Active',
           value: 'Active',
         },
         {
           name: 'Active - Death Claim Pend',
           value: 'Active - Death Claim Pend',
         },
         {
           name: 'Active - ETI',
           value: 'Active - ETI',
         },
         {
           name: 'Active - Non Billing',
           value: 'Active - Non Billing',
         },
         {
           name: 'Active - Ported',
           value: 'Active - Ported',
         },

         {
           name: 'Active - Premium Paying',
           value: 'Active - Premium Paying',
         },
         {
           name: 'Active - Premium Waiver',
           value: 'Active - Premium Waiver',
         },
         {
           name: 'Active-Replaced',
           value: 'Active-Replaced',
         },
         {
           name: 'Active - Single Premium',
           value: 'Active - Single Premium',
         },
         {
           name: 'Initial Premium Due',
           value: 'Initial Premium Due',
         },

         {
           name: 'Pending',
           value: 'Pending',
         },
         {
           name: 'Terminated',
           value: 'Terminated',
         },
         {
           name: 'Terminated - COBRA',
           value: 'Terminated - COBRA',
         },
         {
           name: 'Terminated - Ported',
           value: 'Terminated - Ported',
         },
         {
           name: 'Withdrawn',
           value: 'Withdrawn',
         },

         {
           name: 'Cancelled',
           value: 'cancelled',
         },
         {
           name: 'Declined',
           value: 'Declined',
         },
         {
           name: 'Issued',
           value: 'issued',
         },
       ];

export const PolicyTypes: FilterEnum[] = [
  {
    name: 'Accident',
    value: 'Accident',
  },
  {
    name: 'Annuity',
    value: 'Annuity',
  },
  {
    name: 'Cancer',
    value: 'Cancer',
  },
  {
    name: 'Critical Illness',
    value: 'Critical Illness',
  },
  {
    name: 'Dental',
    value: 'Dental',
  },
  {
    name: 'Disability',
    value: 'Disability',
  },
  {
    name: 'Excess Interest Whole Life',
    value: 'Excess Interest Whole Life',
  },
  {
    name: 'GoodForLife',
    value: 'GoodForLife',
  },
  {
    name: 'Group',
    value: 'Group',
  },
  {
    name: 'Group Accident',
    value: 'Group Accident',
  },
  {
    name: 'Group Term to 100',
    value: 'Group Term to 100',
  },
  {
    name: 'Group Universal Life 23',
    value: 'Group Universal Life 23',
  },
  {
    name: 'Group Voluntary Critical Illness',
    value: 'Group Voluntary Critical Illness',
  },
  {
    name: 'Group Whole Life',
    value: 'Group Whole Life',
  },
  {
    name: 'Heart Stroke',
    value: 'Heart Stroke',
  },
  {
    name: 'Hospital Indemnity',
    value: 'Hospital Indemnity',
  },
  {
    name: 'Individual Whole Life',
    value: 'Individual Whole Life',
  },
  {
    name: 'Life',
    value: 'Life',
  },
  {
    name: 'Long Term Care',
    value: 'Long Term Care',
  },
  {
    name: 'PinnacleCare Health Advisory Services',
    value: 'PinnacleCare Health Advisory Services',
  },
  {
    name: 'Product',
    value: 'Product',
  },
  {
    name: 'SHOP',
    value: 'SHOP',
  },
  {
    name: 'Single Premium Whole Life',
    value: 'Single Premium Whole Life',
  },
  {
    name: 'Term Life',
    value: 'Term Life',
  },
  {
    name: 'Term Life 10 Year',
    value: 'Term Life 10 Year',
  },
  {
    name: 'Universal Life',
    value: 'Universal Life',
  },
];
